import classnames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useInterval } from 'react-use';

import HorizontalMovieListSkeleton from '@/app/[locale]/(default-layout)/_components/skeletons/HorizontalMovieListSkeleton';

import HorizontalMovieListItem from '@/components/HorizontalMovieListItem';

import { styleFaded } from '@cineamo/ui/src/helpers/style-helper';

import { CachedContentData } from '@/hooks/useCachedContentData';
import useScrollState from '@cineamo/ui/src/hooks/useScrollState';

import BackAndForthButtonOverlay from '@cineamo/ui/src/button/BackAndForthButtonOverlay';
import ListLoadingSpinner from '@cineamo/ui/src/loading/ListLoadingSpinner';

import { ComponentAnalytics } from '@/utils/tracking-helper';

export type HorizontalMovieListProps = {
    className?: string;
    contents: CachedContentData[];
    onSelectedContentChange: (
        current: CachedContentData | null,
        next: CachedContentData | null,
        isFading: boolean
    ) => void;
    paginationLoaderRef?: (node?: Element) => void;
    paginationIsFetching?: boolean;
    paginationHasNextPage?: boolean;
    isAutoScrollingEnabled: boolean;
    isEnabled: boolean;
    onPointerDown?: () => void;
    analytics?: ComponentAnalytics;
    showReleaseDate?: boolean;
};

function HorizontalMovieList(props: HorizontalMovieListProps): React.JSX.Element {
    const {
        className,
        contents,
        paginationLoaderRef,
        paginationIsFetching,
        paginationHasNextPage,
        onSelectedContentChange,
        isAutoScrollingEnabled,
        onPointerDown,
        isEnabled,
        analytics,
        showReleaseDate
    } = props;

    const movieScrollContainerRef = useRef<HTMLDivElement>(null);

    const [isInitialized, setIsInitialized] = useState(false);
    const [selectedContentIndex, setSelectedContentIndex] = useState(0);
    const [currentContent, setCurrentContent] = useState<CachedContentData | null>(null);
    const [nextContent, setNextContent] = useState<CachedContentData | null>(null);
    const [isFading, setIsFading] = useState(false);

    const highlightedContent = isFading ? currentContent : nextContent;

    const { styleFadedState } = useScrollState({
        scrollContainerRef: movieScrollContainerRef,
        initialState: {
            startReached: true,
            endReached: false
        }
    });

    // Scroll to the selected item when it changes
    useEffect(() => {
        if (!isAutoScrollingEnabled) {
            return;
        }
        if (movieScrollContainerRef.current) {
            const container = movieScrollContainerRef.current;
            const selectedElement = container.getElementsByClassName(
                '__selected-horizontal-movie-list-item'
            )?.[0] as HTMLDivElement;
            if (!selectedElement) {
                return;
            }
            const offset = selectedElement.offsetLeft - container.offsetWidth / 2 + selectedElement.offsetWidth / 2;
            container.scrollTo({
                left: offset,
                behavior: 'smooth'
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(highlightedContent), isAutoScrollingEnabled]);

    useEffect(() => {
        const firstContent = contents?.[0];
        if (!isInitialized && firstContent && isEnabled !== false) {
            selectMovie(0);
            setIsInitialized(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contents, isEnabled]);

    useInterval(() => {
        if (isInitialized && contents?.length > 1 && isAutoScrollingEnabled) {
            selectNextContent();
        }
    }, 10000);

    function selectMovie(index: number) {
        const newSelectedMovieIndex = index;
        const movie = contents?.[newSelectedMovieIndex];
        setSelectedContentIndex(newSelectedMovieIndex);
        if (isFading && movie !== currentContent) {
            setNextContent(movie);
            onSelectedContentChange(currentContent, movie, false);
            setIsFading(false);
        } else if (movie !== nextContent) {
            setCurrentContent(movie);
            onSelectedContentChange(movie, nextContent, true);
            setIsFading(true);
        }
    }

    function onItemHover(_: CachedContentData, index: number) {
        selectMovie(index);
    }

    function selectNextContent() {
        const newSelectedMovieIndex = (selectedContentIndex + 1) % contents.length;
        selectMovie(newSelectedMovieIndex);
    }

    function handlePointerDown() {
        onPointerDown?.();
    }

    return (
        <div className={classnames(className)}>
            <div className="flex flex-col lg:flex-row space-x-0 lg:space-x-72 space-y-24 lg:space-y-0 items-start">
                <div className="relative w-full">
                    <div
                        className="relative flex flex-row overflow-x-auto space-x-16 no-scrollbar"
                        style={styleFaded(styleFadedState, 5)}
                        onPointerDown={handlePointerDown}
                        ref={movieScrollContainerRef}>
                        {contents?.map((content, index) => (
                            <div
                                key={`HorizontalMovieListItem#${content.id}`}
                                className={classnames('flex flex-col space-y-8', {
                                    '__selected-horizontal-movie-list-item':
                                        highlightedContent && highlightedContent.id === content.id
                                })}>
                                <HorizontalMovieListItem
                                    content={content}
                                    selectedContentItem={highlightedContent ?? undefined}
                                    onMovieItemHover={onItemHover}
                                    index={index}
                                    analytics={analytics}
                                    showReleaseDate={showReleaseDate}
                                />
                            </div>
                        ))}

                        {paginationHasNextPage !== false && (
                            <HorizontalMovieListSkeleton ref={(node) => paginationLoaderRef?.(node ?? undefined)} />
                        )}

                        {paginationHasNextPage !== false && (
                            <ListLoadingSpinner
                                className={classnames(
                                    'flex-none pr-24 w-min',
                                    { 'opacity-0': !paginationIsFetching },
                                    { 'w-auto ': contents?.length > 0 }
                                )}
                            />
                        )}
                    </div>
                    {contents?.length > 0 && (
                        <BackAndForthButtonOverlay
                            scrollContainerRef={movieScrollContainerRef}
                            renderOutside
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

export default HorizontalMovieList;
